import { useRef, useContext, useEffect, useState, useMemo } from 'react'
import UploadFiles from './upload-files'
import Blur from '../ui/shared/blur'
import { CreateProjectDispatch } from './create-project'
import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import XYZSource from 'ol/source/XYZ'
import PreviewMapLayer from './preview-map-layer'
import { createInteractivePolygonStyle, maxOrthoZoom } from '../../ol-styles'
import GeoTIFFMetadata from './geotiff-metadata'
import FilePicker from './file-picker'
import Button from '../ui/elements/button'
import PropTypes from 'prop-types'
import { featureCollection } from '@turf/helpers'

export default function PreviewGeoTIFF({
  files,
  metaData,
  mapboxToken,
  farms,
  farmId,
  fields,
  fieldName,
  crop,
  crops,
  status,
  units,
  projectId,
  projectType,
  options,
  validationMessage,
  isMissingFiles,
  gcpEnabled,
  apiClient,
  stripeToken,
  purchasesEnabled,
  uploadedBytes,
  totalBytes,
  canAddNew,
}) {
  const dispatch = useContext(CreateProjectDispatch)
  const mapContainerRef = useRef()
  const [map, setMap] = useState(null)
  const polygonStyle = useMemo(() => createInteractivePolygonStyle(), [])

  const metaDataInitialized = metaData && metaData.features.length > 0

  useEffect(() => {
    const map = new Map({
      target: mapContainerRef.current,
      logo: false,
      controls: [],
      layers: [
        new TileLayer({
          source: new XYZSource({
            maxZoom: 17,
            tileSize: [512, 512],
            url:
              'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/{z}/{x}/{y}?access_token=' +
              mapboxToken,
          }),
        }),
      ],
      view: new View({
        maxZoom: maxOrthoZoom,
        center: [0, 0],
        zoom: 3,
      }) /* 24 corresponds to about 1cm/px */,
    })
    setMap(map)

    return () => {
      setMap(undefined)
      map.setTarget(null)
    }
  }, [mapboxToken, mapContainerRef])

  const fileValues = files && Object.values(files)
  const file = fileValues && fileValues[0] && fileValues[0].file
  const invalidFile = file && metaData && metaData.features.length === 0
  const canAddFile = canAddNew || !fileValues[0].file

  return (
    <>
      <div className="tw-relative tw--mt-5 tw-custom-border-top">
        <div className="tw-flex tw-flex-col tw-items-end tw-justify-between tw-h-remove-nav">
          <div className="tw-flex tw-flex-col tw-justify-between tw-max-w-sm tw-h-full tw-w-1/4 tw-bg-white tw-z-10 tw-p-6">
            <div className="tw-flex tw-flex-col">
              {status === 'success' && <Blur />}
              {!invalidFile ? (
                <div className="tw-mb-3">
                  <GeoTIFFMetadata
                    file={file}
                    imageFeature={metaData && metaData.features[0]}
                    units={units}
                    setImageProperties={properties => {
                      dispatch({
                        type: 'UPDATE_METADATA',
                        payload: {
                          geojson: featureCollection([
                            { ...metaData.features[0], properties },
                          ]),
                          exif: [],
                        },
                      })
                      dispatch({
                        type: 'SET_PROCESSING_OPTION',
                        payload: {
                          key: 'bands',
                          value: properties.bands,
                        },
                      })
                    }}
                  />
                </div>
              ) : (
                <div>
                  Could not read selected file, please make sure it is a valid
                  GeoTIFF.
                </div>
              )}
              {canAddFile ? (
                <Button
                  size="medium"
                  action="primary-light"
                  className="tw-my-3"
                  onClick={() => {
                    dispatch({ type: 'RESET_FILES' })
                  }}>
                  {!isMissingFiles ? 'Select File' : 'Add Missing File'}
                </Button>
              ) : null}
              {!invalidFile && !file && (
                <FilePicker
                  multiple={false}
                  accept=".tif, .tiff"
                  onChange={files =>
                    dispatch({ type: 'UPDATE_FILES', payload: files })
                  }
                  value={files}
                />
              )}
            </div>
            <UploadFiles
              apiClient={apiClient}
              stripeToken={stripeToken}
              projectId={projectId}
              projectType={projectType}
              crops={crops}
              crop={crop}
              farms={farms}
              farmId={farmId}
              fields={fields}
              fieldName={fieldName}
              files={files}
              metaData={metaData}
              status={status}
              minFiles={1}
              onFarmChange={farmId =>
                dispatch({ type: 'UPDATE_FARM_ID', payload: farmId })
              }
              onFieldNameChange={fieldName =>
                dispatch({ type: 'UPDATE_FIELD_NAME', payload: fieldName })
              }
              onCropChange={crop =>
                dispatch({ type: 'UPDATE_CROP', payload: crop })
              }
              onPermissionsUpdated={async () => {
                dispatch({
                  type: 'UPDATE_PERMISSIONS',
                  payload: await apiClient.getCurrentPermissions(),
                })
              }}
              onUpload={() => dispatch({ type: 'BEGIN_UPLOAD' })}
              options={options}
              onOptionChange={(key, active) =>
                dispatch({ type: 'SET_OPTION', payload: { key, active } })
              }
              validationMessage={validationMessage}
              gcpEnabled={gcpEnabled}
              purchasesEnabled={purchasesEnabled}
              onAddFiles={files =>
                dispatch({ type: 'ADD_EXTRA_FILES', payload: files })
              }
              uploadProgress={
                totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : undefined
              }
            />
          </div>
        </div>
      </div>
      <div
        ref={mapContainerRef}
        className={`tw-w-full tw-absolute tw-top-0 tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white ${
          !metaDataInitialized ? 'tw-invisible' : ''
        }`}
      />
      {map && metaData && (
        <PreviewMapLayer
          map={map}
          metaData={metaData}
          uploaded={new Set()}
          style={polygonStyle}
          onLayerCreated={() => {}}
          onLayerUpdate={() => {}}
          onLayerRemoved={() => {}}
        />
      )}
    </>
  )
}

PreviewGeoTIFF.propTypes = {
  files: PropTypes.object,
  metaData: PropTypes.object,
  mapboxToken: PropTypes.string,
  farms: PropTypes.array,
  farmId: PropTypes.number,
  fields: PropTypes.array,
  fieldName: PropTypes.string,
  crop: PropTypes.string,
  crops: PropTypes.array,
  status: PropTypes.string,
  units: PropTypes.string,
  projectId: PropTypes.number,
  projectType: PropTypes.string,
  options: PropTypes.array,
  validationMessage: PropTypes.string,
  isMissingFiles: PropTypes.bool,
  gcpEnabled: PropTypes.bool,
  apiClient: PropTypes.object,
  stripeToken: PropTypes.string,
  purchasesEnabled: PropTypes.bool,
  uploadedBytes: PropTypes.number,
  totalBytes: PropTypes.number,
  canAddNew: PropTypes.bool,
}
